<template>
  <div v-if="device.serialnumber" class="container advancedSettings">
    <h1 class="title">
      <span class="icon clickable returnIcon" @click="goBack">
        <i class="mdi mdi-chevron-left"></i>
      </span>
      {{ device.data.name }}
    </h1>

    <h2 class="subtitle">{{ $t("device.edit") }} {{ device.data.name }}</h2>

    <div class="columns">
      <div class="column">
        <b-field :label="$t('device.data.name')">
          <b-input v-model="form.name"></b-input>
        </b-field>
      </div>
    </div>

    <div
      class="columns"
      v-if="
        device.data.type !== deviceTypes.therm &&
        device.data.type !== deviceTypes.oval_towel
      "
    >
      <div class="column preview">
        <div
          class="image-preview"
          v-if="imagePreview"
          :style="{ background: form.color }"
        >
          <img :src="imagePreview" alt="preview" />
        </div>

        <div class="field">
          <label class="label">{{ $t("device.data.color") }}</label>
          <div class="columns">
            <div
              v-for="color in colors"
              class="column colorContainer"
              :key="color"
            >
              <div
                v-bind:class="{ colorActive: isColorActive(color) }"
                @click="form.color = color"
                v-bind:style="{ backgroundColor: color }"
                class="color"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="columns" v-if="device.data.type === deviceTypes.therm">
      <div class="column">
        <b-field :label="$t('device.data.nominal_power') + ' (W)'">
          <b-input
            min="140"
            max="3600"
            type="number"
            v-model.number="form.nominal_power"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns" v-if="device.data.type === deviceTypes.therm">
      <div class="column">
        <b-field :label="$t('device.data.tpfl') + ': ' + form.tpfl + 'º'">
          <range-input
            style="width: 100%"
            :min="27"
            :max="40"
            v-model="form.tpfl"
          >
            <template slot="start">
              <span class="rangeMin">27 º</span>
            </template>
            <template slot="end">
              <span class="rangeMax">40 º</span>
            </template>
          </range-input>
        </b-field>
      </div>
    </div>

    <div
      class="columns field"
      v-if="
        device.data.backlight !== undefined &&
        (device.data.type === constants.deviceTypes.radiator ||
          device.data.type === constants.deviceTypes.radiatorb ||
          device.data.type === constants.deviceTypes.towel ||
          device.data.type === constants.deviceTypes.acs ||
          device.data.type === constants.deviceTypes.therm ||
          device.data.type === constants.deviceTypes.oval_towel)
      "
    >
      <div class="column">
        <label class="label">
          {{
            device.data.type === constants.deviceTypes.therm ||
            device.data.type === constants.deviceTypes.oval_towel
              ? $t("settings.backlightTerm")
              : $t("settings.backlight")
          }}
          {{ form.backlight }}%
        </label>
        <range-input
          :min="ranges.backlight.min"
          :max="ranges.backlight.max"
          :step="ranges.backlight.step"
          v-model="form.backlight"
        >
          <template slot="start">
            <span class="rangeMin">{{ ranges.backlight.min }}%</span>
          </template>
          <template slot="end">
            <span class="rangeMax">{{ ranges.backlight.max }}%</span>
          </template>
        </range-input>
      </div>

      <div
        class="column"
        v-if="
          device.data.type === constants.deviceTypes.therm ||
          device.data.type === constants.deviceTypes.oval_towel
        "
      >
        <label class="label">
          {{ $t("settings.backlightTime") }} {{ form.backlight_time }}´´
        </label>
        <range-input
          :min="ranges.backlight.time.min"
          :max="ranges.backlight.time.max"
          :step="ranges.backlight.time.step"
          v-model="form.backlight_time"
        >
          <template slot="start">
            <span class="rangeMin"> {{ ranges.backlight.time.min }}´´ </span>
          </template>
          <template slot="end">
            <span class="rangeMax"> {{ ranges.backlight.time.max }}´´ </span>
          </template>
        </range-input>
      </div>

      <div
        class="column"
        v-if="
          device.data.type === constants.deviceTypes.acs ||
          device.data.type === constants.deviceTypes.radiator ||
          device.data.type === constants.deviceTypes.radiatorb ||
          device.data.type === constants.deviceTypes.towel
        "
      >
        <label class="label">
          {{ $t("settings.backlightOn") }} {{ form.backlight_on }}%
        </label>
        <range-input
          :min="ranges.backlight_on.min"
          :max="ranges.backlight_on.max"
          :step="ranges.backlight_on.step"
          v-model="form.backlight_on"
        >
          <template slot="start">
            <span class="rangeMin"> {{ ranges.backlight_on.min }}% </span>
          </template>
          <template slot="end">
            <span class="rangeMax"> {{ ranges.backlight_on.max }}% </span>
          </template>
        </range-input>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <button
          :disabled="!isFormValid"
          @click="update"
          class="button is-success"
          style="margin-left: 1vw; margin-bottom: 1vw"
        >
          {{ $t("buttons.save") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import constants from "@/config/constants";
import RangeInput from "@/components/shared/RangeInput.vue";

export default {
  name: "device-wizard",
  components: { RangeInput },
  data() {
    return {
      constants: constants,

      form: {
        name: "",
        color: "",
        nominal_power: "",
        backlight: 0,
        backlight_time: 0,
        backlight_on: 0,
        tpfl: null,
      },

      colors: [
        "#f2ff00",
        "#0dff00",
        "#93e800",
        "#f20000",
        "#ff0080",
        "#cc00ff",
        "#0000ff",
        "#0fd1a0",
        "#ffa600",
        "#000000",
      ],

      deviceTypes: constants.deviceTypes,
    };
  },
  computed: {
    ...mapState("installations", {
      installation: "current",
    }),

    ...mapState("zones", {
      zone: "current",
    }),

    ...mapState("devices", {
      device: "current",
    }),

    backlightOnParameters() {
      switch (this.device.data.type) {
        case constants.deviceTypes.radiatorb:
          return constants.ranges.backlight.on.radiator;

        case constants.deviceTypes.radiator:
          return constants.ranges.backlight.on.radiator;

        case constants.deviceTypes.acs:
          return constants.ranges.backlight.on.acs;

        case constants.deviceTypes.towel:
          return constants.ranges.backlight.on.towel;

        default:
          return null;
      }
    },

    imagePreview() {
      switch (this.device.data.type) {
        case constants.deviceTypes.therm:
          return "/home/images/preview/device_thermostat.png";

        case constants.deviceTypes.acs:
          return "/home/images/preview/device_acs.png";

        case constants.deviceTypes.radiatorb:
          return "/home/images/preview/device_radiatorb.png";

        case constants.deviceTypes.radiator:
          // rointe connect radiators has the same image as therms
          return "/home/images/preview/device_thermostat.png";

        case constants.deviceTypes.towel:
          return "/home/images/preview/device_towel.png";

        default:
          return null;
      }
    },

    isFormValid() {
      return (
        this.form.name.length > 0 &&
        (this.device.data.type === constants.deviceTypes.therm
          ? this.form.nominal_power >= 140 && this.form.nominal_power <= 3600
          : true)
      );
    },

    ranges: function () {
      let backlight = {};
      let backlight_on = {};
      if (this.device.data.type === constants.deviceTypes.radiatorb) {
        backlight_on.min = constants.ranges.backlight.on.radiator.min;
        backlight.min = constants.ranges.backlight.radiator.min;
      } else {
        backlight_on.min = constants.ranges.backlight.on.min;
        backlight.min = constants.ranges.backlight.min;
      }

      if (this.device.data.type === constants.deviceTypes.radiatorb) {
        backlight_on.max = constants.ranges.backlight.on.radiator.max;
        backlight.max = constants.ranges.backlight.radiator.max;
      } else {
        backlight_on.max = constants.ranges.backlight.on.max;
        backlight.max = constants.ranges.backlight.max;
      }

      if (this.device.data.type === constants.deviceTypes.radiatorb) {
        backlight_on.step = constants.ranges.backlight.on.radiator.step;
        backlight.step = constants.ranges.backlight.radiator.step;
      } else {
        backlight_on.step = constants.ranges.backlight.on.step;
        backlight.step = constants.ranges.backlight.step;
      }

      backlight.time = {
        min:
          this.device.data.type === constants.deviceTypes.radiatorb
            ? constants.ranges.backlight.time.radiator.min
            : constants.ranges.backlight.time.min,
        max:
          this.device.data.type === constants.deviceTypes.radiatorb
            ? constants.ranges.backlight.time.radiator.max
            : constants.ranges.backlight.time.max,
        step:
          this.device.data.type === constants.deviceTypes.radiatorb
            ? constants.ranges.backlight.time.radiator.step
            : constants.ranges.backlight.time.step,
      };

      return {
        backlight,
        backlight_on,
        backlight_time: {
          min:
            this.device.data.type === constants.deviceTypes.radiatorb
              ? constants.ranges.backlight.time.radiator.min
              : constants.ranges.backlight.time.min,
          max:
            this.device.data.type === constants.deviceTypes.radiatorb
              ? constants.ranges.backlight.time.radiator.max
              : constants.ranges.backlight.time.max,
          step:
            this.device.data.type === constants.deviceTypes.radiatorb
              ? constants.ranges.backlight.time.radiator.step
              : constants.ranges.backlight.time.step,
        },
      };
    },
  },

  methods: {
    update: function () {
      if (
        this.device.data.type === constants.deviceTypes.oval_towel ||
        this.device.data.type === constants.deviceTypes.radiatorb
      ) {
        this.form.color = null;
      } else {
        this.form.color = this.form.color ? this.form.color : "#000000";
      }

      if (this.form.backlight || this.form.backlight === 0) {
        this.form.backlight = this.percentageNumber(this.form.backlight);
      } else {
        this.form.backlight = null;
      }

      if (this.form.backlight_on || this.form.backlight_on === 0) {
        this.form.backlight_on = this.percentageNumber(this.form.backlight_on);
      } else {
        this.form.backlight_on = null;
      }

      this.form.color = this.form.color ? this.form.color : null;
      this.updateDevice({ device: this.device, payload: this.form }).then(
        () => {
          this.goBack();
        }
      );
    },

    isColorActive(color) {
      return color === this.form.color;
    },

    goBack: function () {
      this.$router.push(
        "/installations/" +
          this.installation[".key"] +
          "/zone/" +
          this.zone.path.split("/")[this.zone.path.split("/").length - 1] +
          "/device/" +
          this.device.serialnumber
      );
    },

    numberPercentage(backlight) {
      if (this.device.data.type === constants.deviceTypes.radiatorb) {
        switch (backlight) {
          case constants.ranges.backlight.radiator.converter.val0:
            return constants.ranges.backlight.radiator.converter.per0;

          case constants.ranges.backlight.radiator.converter.val1:
            return constants.ranges.backlight.radiator.converter.per1;

          case constants.ranges.backlight.radiator.converter.val2:
            return constants.ranges.backlight.radiator.converter.per2;

          case constants.ranges.backlight.radiator.converter.val3:
            return constants.ranges.backlight.radiator.converter.per3;

          case constants.ranges.backlight.radiator.converter.val4:
            return constants.ranges.backlight.radiator.converter.per4;

          default:
            return backlight;
        }
      } else {
        switch (backlight) {
          case constants.ranges.backlight.oval_towel.converter.val0:
            return constants.ranges.backlight.oval_towel.converter.per0;

          case constants.ranges.backlight.oval_towel.converter.val1:
            return constants.ranges.backlight.oval_towel.converter.per1;

          case constants.ranges.backlight.oval_towel.converter.val2:
            return constants.ranges.backlight.oval_towel.converter.per2;

          case constants.ranges.backlight.oval_towel.converter.val3:
            return constants.ranges.backlight.oval_towel.converter.per3;

          case constants.ranges.backlight.oval_towel.converter.val4:
            return constants.ranges.backlight.oval_towel.converter.per4;

          case constants.ranges.backlight.oval_towel.converter.val5:
            return constants.ranges.backlight.oval_towel.converter.per5;

          case constants.ranges.backlight.oval_towel.converter.val6:
            return constants.ranges.backlight.oval_towel.converter.per6;

          case constants.ranges.backlight.oval_towel.converter.val7:
            return constants.ranges.backlight.oval_towel.converter.per7;

          case constants.ranges.backlight.oval_towel.converter.val8:
            return constants.ranges.backlight.oval_towel.converter.per8;

          case constants.ranges.backlight.oval_towel.converter.val9:
            return constants.ranges.backlight.oval_towel.converter.per9;

          case constants.ranges.backlight.oval_towel.converter.val10:
            return constants.ranges.backlight.oval_towel.converter.per10;

          default:
            return backlight;
        }
      }
    },

    percentageNumber(backlight) {
      if (this.device.data.type === constants.deviceTypes.radiatorb) {
        switch (backlight) {
          case constants.ranges.backlight.radiator.converter.per0:
            return constants.ranges.backlight.radiator.converter.val0;

          case constants.ranges.backlight.radiator.converter.per1:
            return constants.ranges.backlight.radiator.converter.val1;

          case constants.ranges.backlight.radiator.converter.per2:
            return constants.ranges.backlight.radiator.converter.val2;

          case constants.ranges.backlight.radiator.converter.per3:
            return constants.ranges.backlight.radiator.converter.val3;

          case constants.ranges.backlight.radiator.converter.per4:
            return constants.ranges.backlight.radiator.converter.val4;

          default:
            return backlight;
        }
      } else {
        switch (backlight) {
          case constants.ranges.backlight.oval_towel.converter.per0:
            return constants.ranges.backlight.oval_towel.converter.val0;

          case constants.ranges.backlight.oval_towel.converter.per1:
            return constants.ranges.backlight.oval_towel.converter.val1;

          case constants.ranges.backlight.oval_towel.converter.per2:
            return constants.ranges.backlight.oval_towel.converter.val2;

          case constants.ranges.backlight.oval_towel.converter.per3:
            return constants.ranges.backlight.oval_towel.converter.val3;

          case constants.ranges.backlight.oval_towel.converter.per4:
            return constants.ranges.backlight.oval_towel.converter.val4;

          case constants.ranges.backlight.oval_towel.converter.per5:
            return constants.ranges.backlight.oval_towel.converter.val5;

          case constants.ranges.backlight.oval_towel.converter.per6:
            return constants.ranges.backlight.oval_towel.converter.val6;

          case constants.ranges.backlight.oval_towel.converter.per7:
            return constants.ranges.backlight.oval_towel.converter.val7;

          case constants.ranges.backlight.oval_towel.converter.per8:
            return constants.ranges.backlight.oval_towel.converter.val8;

          case constants.ranges.backlight.oval_towel.converter.per9:
            return constants.ranges.backlight.oval_towel.converter.val9;

          case constants.ranges.backlight.oval_towel.converter.per10:
            return constants.ranges.backlight.oval_towel.converter.val10;

          default:
            return backlight;
        }
      }
    },

    ...mapActions("installations", {
      setInstallation: "setCurrent",
    }),

    ...mapActions("zones", {
      setZone: "setCurrent",
    }),

    ...mapActions("devices", {
      setDevice: "setCurrent",
      updateDevice: "update",
    }),
  },

  mounted() {
    this.setInstallation(this.$route.params.id)
      .then(() => {
        this.setZone(this.$route.params.idZone).then(() =>
          this.setDevice(this.$route.params.idDevice).then(() => {
            this.form.name = this.device.data.name;
            this.form.color = this.device.data.color;
            this.form.nominal_power = this.device.data.nominal_power;
            this.form.backlight = this.numberPercentage(
              this.device.data.backlight
            );
            this.form.backlight_time = this.device.data.backlight_time || null;
            this.form.backlight_on = this.numberPercentage(
              this.device.data.backlight_on
            );

            if (this.device.data.type === this.deviceTypes.therm)
              this.form.tpfl = this.device.data.tpfl;
          })
        );
      })
      .catch(() => {
        this.$router.push("/installations");
      });
  },
};
</script>

<style lang="scss" scoped>
.preview {
  display: grid;
  grid-template-columns: 150px 1fr;
  grid-gap: 15px;
  align-items: center;
  padding: 0 30px;
}

.container {
  max-width: 100%;
}

.advancedSettings {
  padding: 0 2rem 2rem;
}

.advancedSettings .vue-range {
  width: 100%;
}

h1.title {
  margin: 3rem 0 5rem;
}

.advancedSettings .columns {
  border-radius: 0;
}

.advancedSettings .columns:nth-child(3) {
  border-radius: 15px 15px 0 0;
}

.advancedSettings .columns:last-child {
  border-radius: 0 0 15px 15px;
}

div.input {
  padding: 5px;
  background-color: white;
  border: 1px solid #dbdbdb;
  color: #363636;
  box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
  border-radius: 4px;

  label {
    cursor: auto;
  }
}

.colorContainer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.color {
  cursor: pointer;
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  margin: 0 auto;
}

.colorActive {
  width: 5vw;
  height: 5vw;
}
</style>
